import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import type { UserPublic } from '@mondy/shared';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { Spinner } from '@/components/Spinner';
import { api } from '@/api';

const POLL_INTERVAL_MS = 2_000;
const POLL_TIMEOUT_MS = 15_000;

/**
 * Stripe lands users here after a successful Checkout. The webhook usually
 * arrives first, but on a slow webhook delivery we may need to poll briefly
 * for the new tier to materialise on `/auth/me`.
 */
export function PaymentSuccessPage() {
  const me = useCurrentUser();
  const qc = useQueryClient();
  // Snapshot the tier we landed with — we're done when it CHANGES, not just
  // when it's non-free. Otherwise a tier-up purchase (e.g. silver → gold)
  // would short-circuit immediately and never reflect the new tier.
  const [initialTier] = useState(() => me?.paymentTier ?? 'free');
  const [done, setDone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let elapsed = 0;
    const interval = window.setInterval(async () => {
      elapsed += POLL_INTERVAL_MS;
      if (cancelled) return;
      try {
        const res = await api.get<{ user: UserPublic | null }>('/api/auth/me');
        // Seed the cache so other consumers (header, profile) see the new
        // tier immediately on next render.
        qc.setQueryData(['auth', 'me'], res.user);
        if (res.user && res.user.paymentTier !== initialTier) {
          window.clearInterval(interval);
          setDone(true);
          return;
        }
      } catch {
        // network blip — keep polling until timeout
      }
      if (elapsed >= POLL_TIMEOUT_MS) {
        window.clearInterval(interval);
        // Even if we time out, mark done so the user isn't stuck spinning.
        // The next /auth/me refetch (e.g. on profile load) will catch up.
        setDone(true);
      }
    }, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [qc, initialTier]);

  // If we already see a different tier than we landed with, we're done.
  const fastDone = me && me.paymentTier !== initialTier;
  const isDone = done || Boolean(fastDone);

  return (
    <main className="mx-auto flex min-h-dvh max-w-md flex-col px-6 py-8">
      <div className="flex flex-1 flex-col items-center justify-center gap-6 text-center">
        <div
          aria-hidden
          style={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            background: 'var(--color-success-soft, rgba(111,208,135,0.16))',
            color: 'var(--color-success, #6fd087)',
            fontSize: 32,
            lineHeight: '56px',
            fontWeight: 700,
          }}
        >
          ✓
        </div>
        <div className="flex flex-col gap-1">
          <h1 className="text-xl font-semibold">You're upgraded!</h1>
          <p className="text-sm text-[color:var(--color-fg-muted)]">
            {me?.paymentTier && me.paymentTier !== 'free'
              ? `Welcome to ${me.paymentTier.charAt(0).toUpperCase()}${me.paymentTier.slice(1)}.`
              : 'Your purchase has been recorded.'}
          </p>
        </div>
        <Link
          to="/profile"
          className="h-11 inline-flex items-center justify-center rounded-xl bg-[color:var(--color-accent)] px-5 text-sm font-medium text-[color:var(--color-accent-fg)]"
        >
          Back to my groups
        </Link>
      </div>
      {!isDone ? <ConfirmingPurchaseModal /> : null}
    </main>
  );
}

/**
 * Non-dismissible overlay shown while we poll `/auth/me` for the new tier.
 * The user shouldn't be able to close this — the underlying page reflects
 * post-confirmation state and isn't accurate until polling completes.
 */
function ConfirmingPurchaseModal() {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirming-purchase-title"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 100,
        background: 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding:
          'max(16px, env(safe-area-inset-top)) 16px max(16px, env(safe-area-inset-bottom))',
      }}
    >
      <div
        className="w-full max-w-[360px] p-6"
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--line)',
          borderRadius: 'var(--radius-lg)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 16,
        }}
      >
        <Spinner size="md" />
        <p
          id="confirming-purchase-title"
          className="text-sm text-[color:var(--color-fg-muted)]"
          style={{ margin: 0 }}
        >
          Confirming your purchase…
        </p>
      </div>
    </div>
  );
}
